import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Pencil, Plus, RefreshCw, Star, Trash2, Users } from 'lucide-react'
import {
  useLoyaltyTiersAdmin,
  useCreateLoyaltyTier,
  useUpdateLoyaltyTier,
  useDeleteLoyaltyTier,
  useLoyaltyMembers,
} from '@/lib/loyalty'
import { userMessage } from '@/api/errors'
import type { LoyaltyTier, LoyaltyTierInput } from '@/types/ecommerce'
import EditModal, { type EditField } from '@/components/crud/EditModal'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'

const fields: EditField[] = [
  { key: 'name', label: 'Tier name', required: true, placeholder: 'e.g. Gold' },
  { key: 'minLifetimeSpend', label: 'Minimum lifetime spend ($)', type: 'number', required: true },
  { key: 'discountPercent', label: 'Discount %', type: 'number', required: true },
  { key: 'sortOrder', label: 'Sort order', type: 'number', required: true },
]

const toInput = (values: Record<string, any>): LoyaltyTierInput => ({
  name: values.name,
  minLifetimeSpend: Number.isFinite(values.minLifetimeSpend) ? values.minLifetimeSpend : 0,
  discountPercent: Number.isFinite(values.discountPercent) ? values.discountPercent : 0,
  sortOrder: Number.isFinite(values.sortOrder) ? values.sortOrder : 0,
})

function money(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
}

function LoyaltyPage() {
  const { data: tiers = [], isLoading, isError, error, isFetching, refetch } = useLoyaltyTiersAdmin()
  const { data: members = [] } = useLoyaltyMembers()
  const create = useCreateLoyaltyTier()
  const update = useUpdateLoyaltyTier()
  const del = useDeleteLoyaltyTier()
  const confirmDelete = useDeleteConfirm()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<LoyaltyTier | null>(null)

  const sorted = [...tiers].sort((a, b) => a.sortOrder - b.sortOrder)

  return (
    <div className="p-2 md:p-4 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Star className="w-6 h-6" /> Loyalty Program
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Tiers unlock an automatic checkout discount — applied for real by OrderService on every order, not just displayed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
            <Plus className="w-4 h-4" /> New Tier
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading tiers…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load tiers: {(error as Error)?.message}.</p>
        ) : sorted.length === 0 ? (
          <p className="p-6 text-gray-500">No tiers yet — create one to launch the program.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Tier</th>
                <th className="px-4 py-2">Minimum spend</th>
                <th className="px-4 py-2">Discount</th>
                <th className="px-4 py-2">Order</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => (
                <tr key={t.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{t.name}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{money(t.minLifetimeSpend)}+</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{t.discountPercent}%</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{t.sortOrder}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditing(t)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit tier">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          confirmDelete(t.name, async () => {
                            await del.mutateAsync(t.id)
                          })
                        }
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                        title="Delete tier"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div>
        <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-gray-800 dark:text-white">
          <Users className="w-5 h-5" /> Enrolled members
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {members.length === 0 ? (
            <p className="p-6 text-gray-500">No customers enrolled yet.</p>
          ) : (
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
                <tr>
                  <th className="px-4 py-2">Customer</th>
                  <th className="px-4 py-2">Enrolled</th>
                  <th className="px-4 py-2">Lifetime spend</th>
                  <th className="px-4 py-2">Tier</th>
                  <th className="px-4 py-2">Discount</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.userId} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{m.userId}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {m.enrolledAt ? new Date(m.enrolledAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{money(m.lifetimeSpend)}</td>
                    <td className="px-4 py-3 text-gray-800 dark:text-gray-100">{m.tierName ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{m.discountPercent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <EditModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        title="New Tier"
        fields={fields}
        initialValues={{ sortOrder: tiers.length }}
        submitLabel="Create"
        onSubmit={async (values) => {
          try {
            await create.mutateAsync(toInput(values))
          } catch (err) {
            throw new Error(userMessage(err))
          }
        }}
      />

      <EditModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title="Edit Tier"
        description={editing?.name}
        fields={fields}
        initialValues={
          editing
            ? {
                name: editing.name,
                minLifetimeSpend: editing.minLifetimeSpend,
                discountPercent: editing.discountPercent,
                sortOrder: editing.sortOrder,
              }
            : {}
        }
        onSubmit={async (values) => {
          if (!editing) return
          try {
            await update.mutateAsync({ id: editing.id, body: toInput(values) })
          } catch (err) {
            throw new Error(userMessage(err))
          }
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/loyalty/')({
  component: LoyaltyPage,
})
